"use client"

import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/context/auth-context"
import { Mail, LogIn, Sparkles, Shield, ChevronDown, Eye, EyeOff } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { LOGIN_EMAIL_CACHE_KEY } from "@/constants/storage"
import { useTheme } from "next-themes"

// Usuarios activos del equipo
const DEMO_EMAILS = [
  { email: "admin@tepeyac.mx", rol: "admin", nombre: "Administrador", campus: "Guadalajara" },
  { email: "yair@tepeyac.mx", rol: "asesor", nombre: "Yair Hernandez", campus: "IT Innovation School Guadalajara" },
  { email: "lulu@tepeyac.mx", rol: "asesor", nombre: "Lulu", campus: "IT Innovation School Guadalajara" },
  { email: "gaby@tepeyac.mx", rol: "asesor", nombre: "Gaby Silva", campus: "IT Innovation School Guadalajara" },
  { email: "yair.demo@tepeyac.mx", rol: "admin", nombre: "Yair Demo", campus: "Sandbox / Pruebas" },
]

// Helper: acortar/mascarar email para la vista compacta
function maskEmail(email) {
  try {
    const [local, domain] = String(email).split("@")
    if (!local || !domain) return email
    const visible = local.length <= 4 ? local : `${local.slice(0, 3)}…`
    return `${visible}@${domain}`
  } catch {
    return email
  }
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return ""
    return localStorage.getItem(LOGIN_EMAIL_CACHE_KEY) ?? ""
  })
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showQuickAccess, setShowQuickAccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const autoLoginRef = useRef(false)

  useEffect(() => {
    if (autoLoginRef.current) return
    if (typeof window === "undefined") return
    // Auto-login solo si ya hay sesión en Supabase Auth (cookie/refresh token)
    // El getCurrentUser en AuthProvider ya maneja eso. Aquí solo rellenamos el email.
    const savedEmail = localStorage.getItem(LOGIN_EMAIL_CACHE_KEY)
    if (savedEmail) setEmail(savedEmail)
    autoLoginRef.current = true
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    if (!email) {
      setError("Por favor ingresa tu email")
      return
    }
    if (!password) {
      setError("Por favor ingresa tu contraseña")
      return
    }

    setLoading(true)

    try {
      const result = await login(email, password)
      if (result.error) {
        setError(result.error.message || "Error al iniciar sesión")
      } else {
        localStorage.setItem(LOGIN_EMAIL_CACHE_KEY, email)
        navigate("/", { replace: true })
      }
    } catch (err) {
      setError(err.message || "Error inesperado")
    } finally {
      setLoading(false)
    }
  }

  // Legacy: login sin password (solo para cuentas demo que aún no migran a Supabase Auth)
  const handleQuickLoginSubmit = async (demoEmail) => {
    setError("")
    setLoading(true)
    try {
      const result = await login(demoEmail)
      if (result.error) {
        setError(result.error.message || "Error al iniciar sesión")
      } else {
        localStorage.setItem(LOGIN_EMAIL_CACHE_KEY, demoEmail)
        navigate("/", { replace: true })
      }
    } catch (err) {
      setError(err.message || "Error inesperado")
    } finally {
      setLoading(false)
    }
  }

  const currentYear = new Date().getFullYear()

  // Logo según tema
  const { resolvedTheme } = useTheme()
  const logoSrc = resolvedTheme === "dark" ? "/IT_Innovation_School_blanco.png" : "/it-logo.webp"

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/20 flex items-center justify-center p-4 sm:p-6">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -left-24 -top-24 h-60 sm:h-80 w-60 sm:w-80 rounded-full bg-gradient-to-br from-indigo-400/20 to-purple-400/20 blur-3xl" />
        <div className="absolute -right-24 -bottom-24 h-60 sm:h-80 w-60 sm:w-80 rounded-full bg-gradient-to-tr from-emerald-400/20 to-cyan-400/20 blur-3xl" />
      </div>

      <div className="relative w-full max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center">
        {/* Left: Branding */}
        <div className="flex flex-col items-center lg:items-start gap-4 sm:gap-6 px-2 sm:px-6">
          <img src={logoSrc} alt="Instituto Tepeyac" className="w-40 sm:w-[250px] h-auto transition-opacity duration-300" />
          <div className="text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
              IT CRM
            </h1>
            <p className="mt-2 text-sm sm:text-lg text-slate-600 dark:text-slate-400 max-w-lg hidden sm:block">
              Sistema de gestión de leads pensado para colegios — organiza, prioriza y cierra inscripciones con confianza.
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 sm:hidden">
              Gestión de leads para colegios
            </p>
            <div className="mt-4 sm:mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-rose-50 to-amber-50 dark:from-rose-900/20 dark:to-amber-900/20 p-2 sm:p-3 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
              <span className="text-xs sm:text-sm font-medium text-amber-700 dark:text-amber-300">Rápido • Intuitivo • Seguro</span>
            </div>
          </div>
        </div>

        {/* Right: Card con formulario */}
        <div className="relative p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-white/90 dark:bg-slate-900/90 shadow-2xl border border-slate-200/50 dark:border-slate-800/50 backdrop-blur">
          <div className="mb-3 sm:mb-4 flex items-center justify-between gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
                <LogIn className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                Iniciar Sesión
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Ingresa con tu correo institucional</p>
            </div>
            <Badge variant="outline" className="text-xs sm:text-sm hidden">v1.0</Badge>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div className="relative">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 sm:mb-2 block">
                Correo electrónico
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Mail className="h-4 w-4" />
                </span>
                <Input
                  type="email"
                  placeholder="tu.email@tepeyac.mx"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="h-11 sm:h-12 pl-10 pr-4 text-sm sm:text-base rounded-xl"
                />
              </div>
            </div>

            <div className="relative">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 sm:mb-2 block">
                Contraseña
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Shield className="h-4 w-4" />
                </span>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="h-11 sm:h-12 pl-10 pr-11 text-sm sm:text-base rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 sm:h-12 text-sm sm:text-base font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md rounded-xl"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 sm:h-5 sm:w-5 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  <span className="text-sm">Iniciando...</span>
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Acceder
                </>
              )}
            </Button>
          </form>

          {/* Accesos rápidos legacy — colapsable y marcado como temporal */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setShowQuickAccess((v) => !v)}
              className="w-full text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 flex items-center justify-center gap-2 hover:text-slate-600 dark:hover:text-slate-400 transition-colors"
            >
              <Shield className="h-3 w-3" />
              {showQuickAccess ? "Ocultar" : "Mostrar"} acceso rápido (legacy, sin contraseña)
              <ChevronDown className={`h-3 w-3 transition-transform ${showQuickAccess ? "rotate-180" : ""}`} />
            </button>
            {showQuickAccess && (
              <div className="mt-3">
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mb-2 italic">
                  ⚠️ Temporal hasta que todos migren a contraseñas reales
                </p>
                <CompactEmailSelector
                  options={DEMO_EMAILS}
                  selectedEmail={email}
                  onSelect={handleQuickLoginSubmit}
                  className="mt-2"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 sm:bottom-6 text-center w-full px-4">
        <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
          © {currentYear} Instituto Tepeyac. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}

// Compact selector component
function CompactEmailSelector({ options = [], selectedEmail, onSelect }) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(selectedEmail || "")
  const ref = useRef(null)

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return
      if (!ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("click", onDoc)
    return () => document.removeEventListener("click", onDoc)
  }, [])

  useEffect(() => setActive(selectedEmail || ""), [selectedEmail])

  return (
    <div ref={ref} className="relative">
      <div className="flex gap-2 items-center">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full text-left inline-flex items-center justify-between gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"
        >
          <div className="min-w-0 flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold text-sm sm:text-base flex-shrink-0">
              {active ? active[0]?.toUpperCase() : "✉"}
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm font-semibold truncate">{active ? (options.find(o => o.email === active)?.nombre || active.split('@')[0]) : "Seleccionar cuenta"}</div>
              <div className="text-[10px] sm:text-xs text-slate-500 truncate">{active || "Elige una cuenta..."}</div>
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
        </button>

        <button
          onClick={() => onSelect(active)}
          disabled={!active}
          className="px-3 sm:px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs sm:text-sm font-semibold shadow-md disabled:opacity-50 flex-shrink-0"
        >
          Usar
        </button>
      </div>

      {open && (
        <ul className="absolute z-50 mt-2 w-full max-h-48 sm:max-h-56 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-1.5 sm:p-2">
          {options.map((opt) => (
            <li
              key={opt.email}
              onClick={() => {
                setActive(opt.email)
                setOpen(false)
              }}
              className="flex items-center gap-2 sm:gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
            >
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-white text-xs sm:text-sm flex-shrink-0 ${opt.rol === 'admin' ? 'bg-amber-500' : 'bg-indigo-500'}`}>
                {opt.nombre.split(" ").map(n => n[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm font-semibold">{opt.nombre}</div>
                <div className="text-[10px] sm:text-xs text-slate-500">{opt.email}</div>
              </div>
              <div className="flex-shrink-0">
                <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${opt.rol === 'admin' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'}`}>
                  {opt.rol === 'admin' ? 'Admin' : 'Asesor'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
